export interface ParsedAddress {
  formattedAddress: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  lat?: number;
  lng?: number;
}

interface AddressComponentLike {
  types: string[];
  long_name?: string;
  short_name?: string;
  longText?: string;
  shortText?: string;
}

const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-places-script';

let loadPromise: Promise<void> | null = null;

const hasGooglePlacesLibrary = () =>
  typeof window.google?.maps?.importLibrary === 'function';

const getAddressComponent = (
  components: AddressComponentLike[],
  type: string,
  nameType: 'long' | 'short' = 'long',
) => {
  const component = components.find((entry) => entry.types.includes(type));
  if (!component) return '';
  if (nameType === 'short') {
    return component.shortText || component.short_name || '';
  }
  return component.longText || component.long_name || '';
};

const readCoordinate = (
  value: number | (() => number) | undefined,
): number | undefined => {
  if (value == null) return undefined;
  return typeof value === 'function' ? value() : value;
};

export const parseAddressComponents = (
  components: AddressComponentLike[],
  formattedAddress = '',
  location?: {
    lat?: number | (() => number);
    lng?: number | (() => number);
  } | null,
): ParsedAddress => {
  const streetNumber = getAddressComponent(components, 'street_number');
  const route = getAddressComponent(components, 'route');
  const subpremise = getAddressComponent(components, 'subpremise');
  const city =
    getAddressComponent(components, 'locality') ||
    getAddressComponent(components, 'postal_town') ||
    getAddressComponent(components, 'sublocality') ||
    getAddressComponent(components, 'administrative_area_level_2');

  return {
    formattedAddress,
    address_line_1: [streetNumber, route].filter(Boolean).join(' '),
    address_line_2: subpremise,
    city,
    state: getAddressComponent(
      components,
      'administrative_area_level_1',
      'short',
    ),
    postal_code: getAddressComponent(components, 'postal_code'),
    country: getAddressComponent(components, 'country', 'short'),
    lat: readCoordinate(location?.lat),
    lng: readCoordinate(location?.lng),
  };
};

export const parseGooglePlaceAddress = (
  place: google.maps.places.PlaceResult,
): ParsedAddress =>
  parseAddressComponents(
    place.address_components || [],
    place.formatted_address || '',
    place.geometry?.location,
  );

export const parseGooglePlaceDetails = (
  place: Pick<
    google.maps.places.Place,
    'addressComponents' | 'formattedAddress' | 'location'
  >,
): ParsedAddress =>
  parseAddressComponents(
    place.addressComponents || [],
    place.formattedAddress || '',
    place.location,
  );

const installGoogleMapsBootstrap = (apiKey: string) => {
  const bootstrap = document.createElement('script');
  bootstrap.id = GOOGLE_MAPS_SCRIPT_ID;
  bootstrap.text = `(g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=\`https://maps.\${c}apis.com/maps/api/js?\`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({key:${JSON.stringify(apiKey)},v:"weekly"});`;
  document.head.appendChild(bootstrap);
};

export const loadGooglePlacesScript = (apiKey: string) => {
  if (!apiKey) {
    return Promise.reject(new Error('Google Maps API key is not configured.'));
  }

  if (hasGooglePlacesLibrary()) {
    return window.google!.maps.importLibrary('places').then(() => undefined);
  }

  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    const previousAuthFailure = window.gm_authFailure;
    window.gm_authFailure = () => {
      loadPromise = null;
      reject(
        new Error(
          'Google Maps rejected the API key. Enable Maps JavaScript API and Places API (New), then check billing and HTTP referrer restrictions.',
        ),
      );
      previousAuthFailure?.();
    };

    const finish = async () => {
      try {
        const { PlaceAutocompleteElement } =
          await window.google!.maps.importLibrary('places');
        if (!PlaceAutocompleteElement) {
          throw new Error('Google Places library failed to initialize.');
        }
        resolve();
      } catch (error) {
        loadPromise = null;
        reject(
          error instanceof Error
            ? error
            : new Error('Google Places library failed to initialize.'),
        );
      }
    };

    const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
    if (existingScript) {
      if (hasGooglePlacesLibrary()) {
        void finish();
        return;
      }
      existingScript.addEventListener('load', () => void finish(), {
        once: true,
      });
      return;
    }

    installGoogleMapsBootstrap(apiKey);
    const waitForImportLibrary = () => {
      if (hasGooglePlacesLibrary()) {
        void finish();
        return;
      }
      window.setTimeout(waitForImportLibrary, 50);
    };
    waitForImportLibrary();
  });

  return loadPromise;
};

const ADDRESS_TYPES = ['street_address', 'premise', 'subpremise'];

/** One row of a programmatic address search; resolve it to get the parts. */
export interface AddressSuggestion {
  id: string;
  label: string;
  primary: string;
  secondary: string;
  prediction: google.maps.places.PlacePrediction;
}

// One session spans the keystrokes of a search and the details call of the
// pick, so Google bills it as a single autocomplete session.
let sessionToken: google.maps.places.AutocompleteSessionToken | null = null;

export const searchAddressSuggestions = async (
  query: string,
  apiKey: string,
): Promise<AddressSuggestion[]> => {
  await loadGooglePlacesScript(apiKey);
  const { AutocompleteSuggestion, AutocompleteSessionToken } =
    await google.maps.importLibrary('places');
  sessionToken ??= new AutocompleteSessionToken();
  const { suggestions } =
    await AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: query,
      sessionToken,
      includedPrimaryTypes: ADDRESS_TYPES,
    });
  return suggestions.flatMap(({ placePrediction: p }) =>
    p
      ? [
          {
            id: p.placeId,
            label: p.text.text,
            primary: p.mainText?.text || p.text.text,
            secondary: p.secondaryText?.text || '',
            prediction: p,
          },
        ]
      : [],
  );
};

export const resolveAddressSuggestion = async (
  suggestion: AddressSuggestion,
): Promise<ParsedAddress> => {
  const place = suggestion.prediction.toPlace();
  // The same token that grouped the keystrokes has to ride on the details
  // call. That is what closes the session, so Google bills one session per
  // pick instead of one request per keystroke.
  const token = sessionToken;
  sessionToken = null;
  await place.fetchFields({
    fields: ['formattedAddress', 'addressComponents', 'location'],
    ...(token ? { sessionToken: token } : {}),
  });
  return parseGooglePlaceDetails(place);
};

export const fetchTimezoneId = async (
  lat: number,
  lng: number,
  apiKey: string,
) => {
  if (!apiKey) return '';

  const timestamp = Math.floor(Date.now() / 1000);
  const url = new URL('https://maps.googleapis.com/maps/api/timezone/json');
  url.searchParams.set('location', `${lat},${lng}`);
  url.searchParams.set('timestamp', String(timestamp));
  url.searchParams.set('key', apiKey);

  try {
    const response = await fetch(url.toString());
    if (!response.ok) return '';
    const data = (await response.json()) as { timeZoneId?: string };
    return data.timeZoneId || '';
  } catch {
    return '';
  }
};
